import React, { useState, useEffect, useContext, useRef } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import { SocketContext } from '../context/SocketContext';
import * as FaIcons from 'react-icons/fa';
import { FocusableSection, FocusableButton, FocusableCard, useBackHandler, setFocus } from '../components/Focusable';
import { API_URL } from '../config';
import { useToast } from '../context/ToastContext';

const Container = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  position: relative;

  @media (max-width: 768px) {
    padding: 1rem;
    padding-top: 70px;
  }
`;

const MenuButton = styled.button`
  position: fixed;
  top: 20px;
  left: 20px;
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #632ce4, #9d50bb);
  border: none;
  border-radius: 12px;
  cursor: pointer;
  display: ${props => props.$show ? 'flex' : 'none'};
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  z-index: 999;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(99, 44, 228, 0.4);

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 20px rgba(99, 44, 228, 0.6);
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 768px) {
    top: 15px;
    left: 15px;
    width: 45px;
    height: 45px;
  }
`;

const MenuLine = styled.div`
  width: 25px;
  height: 3px;
  background: #fff;
  border-radius: 2px;
  transition: all 0.3s ease;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    margin-bottom: 1rem;
  }
`;

const Title = styled.h1`
  color: #fff;
  font-size: 2rem;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const InCallWarning = styled.div`
  background: linear-gradient(135deg, #f39c12, #e67e22);
  color: #fff;
  padding: 1rem 1.5rem;
  border-radius: 8px;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 4px 15px rgba(243, 156, 18, 0.3);

  &::before {
    content: '📞';
    font-size: 1.5rem;
  }
`;

const ContactsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
    margin-top: 1rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const ContactCard = styled.div`
  background: ${props => props.$hasMissedCall
    ? 'linear-gradient(145deg, #e67e22, #d35400)'
    : 'linear-gradient(145deg, #1a1d24, #15171c)'};
  border-radius: 12px;
  padding: 1.5rem;
  text-align: center;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  border: 2px solid ${props => props.$hasMissedCall ? '#f39c12' : 'transparent'};
  position: relative;
  opacity: ${props => props.disabled ? 0.5 : 1};
  box-shadow: ${props => props.$hasMissedCall ? '0 4px 15px rgba(243, 156, 18, 0.5)' : 'none'};
  outline: none;

  &:hover {
    transform: ${props => props.disabled ? 'none' : 'translateY(-5px)'};
    border-color: ${props => {
      if (props.disabled) return 'transparent';
      if (props.$hasMissedCall) return '#f1c40f';
      return '#632ce4';
    }};
    box-shadow: ${props => {
      if (props.disabled) return 'none';
      if (props.$hasMissedCall) return '0 8px 25px rgba(243, 156, 18, 0.7)';
      return '0 8px 20px rgba(99, 44, 228, 0.3)';
    }};
  }
`;

const OnlineStatus = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => {
    if (props.$status === 'inCall') return '#f39c12';
    if (props.$status === 'online') return '#27ae60';
    return '#e74c3c';
  }};
  box-shadow: 0 0 10px ${props => {
    if (props.$status === 'inCall') return 'rgba(243, 156, 18, 0.8)';
    if (props.$status === 'online') return 'rgba(39, 174, 96, 0.8)';
    return 'rgba(231, 76, 60, 0.8)';
  }};
  border: 2px solid #1a1d24;
  z-index: 10;

  ${props => props.$status === 'online' && `
    animation: pulse 2s ease-in-out infinite;
  `}

  @keyframes pulse {
    0%, 100% {
      box-shadow: 0 0 10px rgba(39, 174, 96, 0.8);
    }
    50% {
      box-shadow: 0 0 20px rgba(39, 174, 96, 1);
    }
  }
`;

const Avatar = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #632ce4, #9d50bb);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  font-size: 2rem;
  color: #fff;
  font-weight: bold;

  @media (max-width: 768px) {
    width: 70px;
    height: 70px;
    font-size: 1.75rem;
    margin-bottom: 0.75rem;
  }
`;

const ContactName = styled.h3`
  color: #fff;
  margin: 0.5rem 0;
  font-size: 1.2rem;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin: 0.25rem 0;
  }
`;

const GroupsText = styled.p`
  color: #9d50bb;
  margin: 0.25rem 0;
  font-size: 0.75rem;
  font-weight: 500;
`;

const StatusText = styled.p`
  color: ${props => {
    if (props.$status === 'inCall') return '#f39c12';
    if (props.$status === 'online') return '#27ae60';
    return '#888';
  }};
  margin: 0.5rem 0 0 0;
  font-size: 0.75rem;
  font-weight: 500;
`;

const MissedCallText = styled.p`
  color: #fff;
  margin: 0.5rem 0 0 0;
  font-size: 0.8rem;
  font-weight: 700;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
`;

const AutoAnswerRow = styled.label`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-top: 0.75rem;
  padding: 0.5rem 0.75rem;
  background: rgba(99, 44, 228, 0.1);
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.75rem;
  color: #ddd;
  user-select: none;
`;

const AutoAnswerLabel = styled.span`
  flex: 1;
  text-align: left;
  font-weight: 500;
`;

const Switch = styled.span`
  position: relative;
  display: inline-block;
  width: 36px;
  height: 20px;
  flex-shrink: 0;
  background: ${props => (props.$checked ? '#632ce4' : '#555')};
  border-radius: 20px;
  transition: background 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${props => (props.$checked ? '18px' : '2px')};
    width: 16px;
    height: 16px;
    background: #fff;
    border-radius: 50%;
    transition: left 0.2s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #666;
`;

const EmptyHint = styled.p`
  color: #888;
  font-size: 0.9rem;
  margin-top: 1rem;
`;

const Contacts = ({ sidebar, setCurrentPage, setCallData, isInCall, toggleSidebar }) => {
  const [contacts, setContacts] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [usersInCall, setUsersInCall] = useState(new Set());
  const [missedCalls, setMissedCalls] = useState({});
  const [autoAnswerByContactId, setAutoAnswerByContactId] = useState({});
  const currentTimerRef = useRef(null);
  const lastFocusedContactRef = useRef(null);
  const { user } = useContext(AuthContext);
  const { socket } = useContext(SocketContext);
  const toast = useToast();

  const containerRef = useRef(null);

  // Gestion du back
  useBackHandler(() => {
    if (setCurrentPage) {
      setCurrentPage('home');
    }
  });

  // Focus sur la section quand la sidebar se ferme
  useEffect(() => {
    if (!sidebar) {
      setFocus('CONTACTS_MAIN');
    }
  }, [sidebar]);

  // Fonction pour marquer un appel manque comme "vu"
  const markMissedCallAsSeen = (contactEmail) => {
    if (missedCalls[contactEmail]) {
      console.log(`✅ Appel manque de ${contactEmail} marque comme vu`);
      const newMissedCalls = { ...missedCalls };
      delete newMissedCalls[contactEmail];
      setMissedCalls(newMissedCalls);
      localStorage.setItem('missedCalls', JSON.stringify(newMissedCalls));
      window.dispatchEvent(new CustomEvent('missedCallUpdated'));
    }
  };

  // Surveiller l'element focuse pour gerer les appels manques
  useEffect(() => {
    const checkFocusedElement = () => {
      const focusedElement = document.querySelector('.tv-focused');

      if (!focusedElement) {
        if (currentTimerRef.current) {
          clearTimeout(currentTimerRef.current);
          currentTimerRef.current = null;
        }
        lastFocusedContactRef.current = null;
        return;
      }

      const contactCards = document.querySelectorAll('[data-contact-email]');
      let focusedContact = null;

      contactCards.forEach(card => {
        if (card.classList.contains('tv-focused')) {
          focusedContact = card.getAttribute('data-contact-email');
        }
      });

      if (focusedContact && focusedContact !== lastFocusedContactRef.current) {
        if (currentTimerRef.current) {
          clearTimeout(currentTimerRef.current);
          currentTimerRef.current = null;
        }

        lastFocusedContactRef.current = focusedContact;

        if (missedCalls[focusedContact]) {
          currentTimerRef.current = setTimeout(() => {
            markMissedCallAsSeen(focusedContact);
            currentTimerRef.current = null;
          }, 1000);
        }
      }
    };

    const observer = new MutationObserver(checkFocusedElement);

    if (containerRef.current) {
      observer.observe(containerRef.current, {
        attributes: true,
        attributeFilter: ['class'],
        subtree: true
      });
    }

    checkFocusedElement();

    return () => {
      observer.disconnect();
      if (currentTimerRef.current) {
        clearTimeout(currentTimerRef.current);
      }
    };
  }, [missedCalls, contacts]);

  useEffect(() => {
    loadContacts();

    const loadMissedCalls = () => {
      const savedMissedCalls = localStorage.getItem('missedCalls');
      if (savedMissedCalls) {
        setMissedCalls(JSON.parse(savedMissedCalls));
      }
    };

    loadMissedCalls();

    const handleMissedCallUpdate = () => {
      loadMissedCalls();
    };

    window.addEventListener('missedCallUpdated', handleMissedCallUpdate);

    return () => {
      window.removeEventListener('missedCallUpdated', handleMissedCallUpdate);
    };
  }, []);

  useEffect(() => {
    if (socket) {
      socket.emit('request-online-users');
      socket.emit('request-users-in-call');

      socket.on('online-users-list', (users) => {
        setOnlineUsers(new Set(users));
      });

      socket.on('users-in-call-list', (users) => {
        setUsersInCall(new Set(users));
      });

      socket.on('user-online', (email) => {
        setOnlineUsers(prev => new Set([...prev, email]));
      });

      socket.on('user-offline', (email) => {
        setOnlineUsers(prev => {
          const newSet = new Set(prev);
          newSet.delete(email);
          return newSet;
        });
        setUsersInCall(prev => {
          const newSet = new Set(prev);
          newSet.delete(email);
          return newSet;
        });
      });

      socket.on('user-call-status-changed', (data) => {
        if (data.inCall) {
          setUsersInCall(prev => new Set([...prev, data.email]));
        } else {
          setUsersInCall(prev => {
            const newSet = new Set(prev);
            newSet.delete(data.email);
            return newSet;
          });
        }
      });

      return () => {
        socket.off('online-users-list');
        socket.off('users-in-call-list');
        socket.off('user-online');
        socket.off('user-offline');
        socket.off('user-call-status-changed');
      };
    }
  }, [socket]);

  const loadContacts = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/contacts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContacts(res.data);
    } catch (err) {
      console.error('Erreur chargement contacts:', err);
    }
  };

  const handleCallContact = (contact) => {
    if (isInCall) {
      toast.warning('Vous etes deja en appel. Raccrochez d\'abord avant d\'en demarrer un nouveau.', {
        title: 'Appel en cours'
      });
      return;
    }

    markMissedCallAsSeen(contact.email);

    const roomId = `room-${Date.now()}-${Math.random().toString(36).substring(7)}`;

    setCallData({
      roomId,
      contactEmail: contact.email,
      contactName: `${contact.prenom} ${contact.nom}`,
      isCaller: true,
      autoAnswer: !!(contact.is_senior && autoAnswerByContactId[contact.id])
    });

    setCurrentPage('appels');
  };

  const toggleAutoAnswer = (contactId) => {
    setAutoAnswerByContactId(prev => ({
      ...prev,
      [contactId]: !prev[contactId]
    }));
  };

  const getInitials = (prenom, nom) => {
    const first = prenom?.charAt(0) || '';
    const last = nom?.charAt(0) || '';
    return (first + last).toUpperCase() || '?';
  };

  const isContactOnline = (contactEmail) => {
    return onlineUsers.has(contactEmail);
  };

  const isContactInCall = (contactEmail) => {
    return usersInCall.has(contactEmail);
  };

  const getContactStatus = (contactEmail) => {
    if (isContactInCall(contactEmail)) return 'inCall';
    if (isContactOnline(contactEmail)) return 'online';
    return 'offline';
  };

  const formatMissedCallTime = (timestamp) => {
    const now = new Date();
    const callTime = new Date(timestamp);
    const diffMs = now - callTime;

    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMinutes < 1) {
      return "a l'instant";
    } else if (diffMinutes < 60) {
      return `il y a ${diffMinutes}min`;
    } else if (diffHours < 24) {
      const remainingMinutes = diffMinutes % 60;
      if (remainingMinutes === 0) {
        return `il y a ${diffHours}h`;
      }
      return `il y a ${diffHours}h${remainingMinutes}min`;
    } else {
      const remainingHours = diffHours % 24;
      const remainingMinutes = diffMinutes % 60;
      if (remainingHours === 0 && remainingMinutes === 0) {
        return `il y a ${diffDays}j`;
      } else if (remainingMinutes === 0) {
        return `il y a ${diffDays}j ${remainingHours}h`;
      }
      return `il y a ${diffDays}j ${remainingHours}h${remainingMinutes}min`;
    }
  };

  const hasMissedCall = (contactEmail) => {
    return missedCalls[contactEmail] !== undefined;
  };

  return (
    <FocusableSection focusKey="CONTACTS_MAIN">
      <Container ref={containerRef}>
        <FocusableButton
          as={MenuButton}
          show={!sidebar}
          onPress={toggleSidebar}
        >
          <MenuLine />
          <MenuLine />
          <MenuLine />
        </FocusableButton>

        <Header>
          <Title>Mes Contacts</Title>
        </Header>

        {isInCall && (
          <InCallWarning>
            <div>
              <strong>Appel en cours</strong>
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.9rem' }}>
                Vous etes actuellement en appel. La vignette video est visible en bas a droite.
              </p>
            </div>
          </InCallWarning>
        )}

        {contacts.length === 0 ? (
          <EmptyState>
            <FaIcons.FaUsers style={{ fontSize: '4rem', marginBottom: '1rem', opacity: 0.5 }} />
            <h2>Aucun contact pour le moment</h2>
            <p>Rejoignez un groupe ou creez-en un pour voir vos contacts ici.</p>
            <EmptyHint>
              Allez dans "Mon Compte" pour gerer vos groupes
            </EmptyHint>
          </EmptyState>
        ) : (
          <ContactsGrid>
            {contacts.map((contact) => {
              const status = getContactStatus(contact.email);
              const missed = hasMissedCall(contact.email);
              return (
                <FocusableCard
                  key={contact.id}
                  as={ContactCard}
                  onPress={() => !isInCall && handleCallContact(contact)}
                  disabled={isInCall}
                  $hasMissedCall={missed}
                  data-contact-email={contact.email}
                >
                  <OnlineStatus $status={status} />
                  <Avatar>{getInitials(contact.prenom, contact.nom)}</Avatar>
                  <ContactName>{contact.prenom} {contact.nom}</ContactName>
                  {contact.groups && (
                    <GroupsText>{contact.groups}</GroupsText>
                  )}
                  {missed ? (
                    <MissedCallText>
                      Appel manque {formatMissedCallTime(missedCalls[contact.email].timestamp)}
                    </MissedCallText>
                  ) : (
                    <StatusText $status={status}>
                      {status === 'inCall' ? 'En appel' : status === 'online' ? 'En ligne' : 'Hors ligne'}
                    </StatusText>
                  )}
                  {contact.is_senior && status !== 'inCall' && (
                    <AutoAnswerRow
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleAutoAnswer(contact.id);
                      }}
                    >
                      <AutoAnswerLabel>Decrochage auto</AutoAnswerLabel>
                      <Switch
                        role="switch"
                        aria-checked={!!autoAnswerByContactId[contact.id]}
                        $checked={!!autoAnswerByContactId[contact.id]}
                      />
                    </AutoAnswerRow>
                  )}
                </FocusableCard>
              );
            })}
          </ContactsGrid>
        )}
      </Container>
    </FocusableSection>
  );
};

export default Contacts;
